import { SearchOutlined } from '@ant-design/icons'
import { Checkbox, Col, Input, Row, Select } from 'antd'
import React, { useContext, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import LoadingButton from 'src/components/styled/LoadingButton'
import {
  performSearch,
  setError,
  setFields,
  setPage,
  setProducts,
  setSelectedProductType,
  setSelectedSupplierTitle,
  setTerm,
  setTotal,
} from 'src/features/CatalogueSearchSlice'
import { resetCatalougeFilter } from 'src/features/FilterSlice'
import { useWindowWidth } from 'src/utils/useWindowWidth'
import styled, { ThemeContext } from 'styled-components/macro'

const { Option } = Select

const CATALOGUE_SEARCH_FIELDS = ['title', 'sku']

const SearchBar = ({ venueId, supplierTitles = [], recentSupplier = null }) => {
  const dispatch = useDispatch()
  const searchFields = useSelector(state => state.catalogueSearch.fields)
  const searchTerm = useSelector(state => state.catalogueSearch.term)
  const searchProducts = useSelector(state => state.catalogueSearch.products)
  const pageSize = useSelector(state => state.catalogueSearch.pageSize)
  const searchTotal = useSelector(state => state.catalogueSearch.total)
  const searchLoading = useSelector(state => state.catalogueSearch.loading)
  const searchError = useSelector(state => state.catalogueSearch.error)
  const selectedSupplierTitle = useSelector(
    state => state.catalogueSearch.selectedSupplierTitle,
  )
  const selectedProductType = useSelector(
    state => state.catalogueSearch.selectedProductType,
  )
  const productTypeOptions = useSelector(
    state => state.catalogueSearch.productTypeOptions,
  )
  const supplierFilterName = useSelector(
    state => state.filter.catalogue.supplier,
  )

  const isCatalogue = Boolean(venueId)
  const hasScopeFilter = Boolean(
    isCatalogue && (selectedSupplierTitle || selectedProductType),
  )

  const orderedSupplierTitles = useMemo(() => {
    if (recentSupplier && supplierTitles.includes(recentSupplier)) {
      return [
        recentSupplier,
        ...supplierTitles.filter(title => title !== recentSupplier),
      ]
    }
    return supplierTitles
  }, [supplierTitles, recentSupplier])

  const typeOptions = useMemo(() => {
    const options = [...(productTypeOptions || [])]
    if (
      selectedProductType &&
      !options.some(type => type === selectedProductType)
    ) {
      options.push(selectedProductType)
    }
    return options.sort((a, b) =>
      String(a).trim().localeCompare(String(b).trim()),
    )
  }, [productTypeOptions, selectedProductType])

  const onSearch = () => {
    if (!searchTerm.trim() && !hasScopeFilter) {
      dispatch(setError('Please enter a search term'))
      dispatch(setTerm(''))
    } else {
      dispatch(setError(''))
      dispatch(performSearch({ venueId, pageNumber: 1, pageSize }))
    }
  }

  const applySupplierFilter = supplierName => {
    dispatch(setSelectedSupplierTitle(supplierName || ''))
    dispatch(setError(''))
    dispatch(
      performSearch({
        venueId,
        pageNumber: 1,
        pageSize,
      }),
    )
  }

  const applyTypeFilter = productType => {
    dispatch(setSelectedProductType(productType || ''))
    dispatch(setError(''))
    dispatch(
      performSearch({
        venueId,
        pageNumber: 1,
        pageSize,
      }),
    )
  }

  useEffect(() => {
    if (isCatalogue) {
      dispatch(setFields(CATALOGUE_SEARCH_FIELDS))
    }
  }, [dispatch, isCatalogue])

  useEffect(() => {
    if (supplierFilterName && supplierFilterName.length > 0) {
      dispatch(setSelectedSupplierTitle(supplierFilterName[0]))
      dispatch(resetCatalougeFilter())
      dispatch(setError(''))
      // Catalogue performs the search once venue suppliers are loaded
    }
  }, [])

  const windowWidth = useWindowWidth()
  const theme = useContext(ThemeContext)
  const isMobile = windowWidth <= theme.breakpoints.lg
  const isSupplierPortal = window.location.href.endsWith('supplier/products')

  if (isCatalogue) {
    const resultBits = [
      selectedSupplierTitle || null,
      selectedProductType || null,
    ].filter(Boolean)

    return (
      <CatalogueToolbar>
        <CatalogueSearchInput
          prefix={<SearchOutlined />}
          placeholder='Search by title or SKU...'
          value={searchTerm}
          size='large'
          onChange={event => {
            const value = event.target.value
            dispatch(setError(''))
            dispatch(setTerm(value))
            if (!value) {
              dispatch(performSearch({ venueId, pageNumber: 1, pageSize }))
            }
          }}
          onPressEnter={onSearch}
          allowClear
        />
        {orderedSupplierTitles.length > 0 && (
          <FilterSelect
            id='catalogue-supplier-filter'
            allowClear
            showSearch
            size='large'
            placeholder='All suppliers'
            value={selectedSupplierTitle || undefined}
            onChange={value => applySupplierFilter(value)}
            loading={searchLoading}
            optionFilterProp='children'
            filterOption={(input, option) =>
              String(option.props.children)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {orderedSupplierTitles.map(title => (
              <Option key={title} value={title}>
                {title === recentSupplier
                  ? `${title} (Recently connected)`
                  : title}
              </Option>
            ))}
          </FilterSelect>
        )}
        <FilterSelect
          id='catalogue-type-filter'
          allowClear
          showSearch
          size='large'
          placeholder='All types'
          value={selectedProductType || undefined}
          onChange={value => applyTypeFilter(value)}
          loading={searchLoading}
          optionFilterProp='children'
          filterOption={(input, option) =>
            String(option.props.children)
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {typeOptions.map(type => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </FilterSelect>
        <LoadingButton
          type='primary'
          size='large'
          loading={searchLoading}
          onClick={onSearch}
        >
          Search
        </LoadingButton>
        {Boolean(searchTerm.trim() || hasScopeFilter) &&
          Boolean(searchProducts) && (
            <CatalogueResultCount>
              {searchTotal}
              {searchTotal === 1 ? ' result' : ' results'}
              {resultBits.length ? ` · ${resultBits.join(' · ')}` : ''}
            </CatalogueResultCount>
          )}
        {searchError && (
          <CatalogueSearchError>{searchError}</CatalogueSearchError>
        )}
      </CatalogueToolbar>
    )
  }

  return (
    <Input.Group size='large' style={{ width: '100%', marginBottom: 20 }}>
      <Row gutter={16}>
        <Col span={isMobile ? 24 : 8}>
          <Input
            prefix={<SearchOutlined />}
            placeholder='Search by...'
            value={searchTerm}
            onChange={event => {
              const value = event.target.value
              dispatch(setError(''))
              dispatch(setTerm(value))
              if (!value) {
                dispatch(setTotal(0))
                dispatch(setPage(1))
                dispatch(setProducts(null))
              }
            }}
            onPressEnter={onSearch}
            allowClear
          />
          {searchError && <SearchError>{searchError}</SearchError>}
        </Col>
        <Col
          span={isMobile ? 24 : 6}
          style={{
            minHeight: '40px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Checkbox.Group
            options={
              isSupplierPortal
                ? [
                    { label: 'Title', value: 'title' },
                    { label: 'SKU', value: 'sku' },
                  ]
                : [
                    { label: 'Title', value: 'title' },
                    { label: 'SKU', value: 'sku' },
                    { label: 'Supplier', value: 'supplierTitle' },
                  ]
            }
            value={searchFields}
            onChange={checkedValues => dispatch(setFields(checkedValues))}
          />
        </Col>
        <Col span={isMobile ? 12 : 2}>
          <LoadingButton
            type='primary'
            size='large'
            loading={searchLoading}
            onClick={onSearch}
          >
            Search
          </LoadingButton>
        </Col>
        <Col span={isMobile ? 12 : 8}>
          {Boolean(searchTerm.trim()) && Boolean(searchProducts) && (
            <SearchResultCount>
              {searchTotal}
              {searchTotal === 1 ? ' result' : ' results'}
            </SearchResultCount>
          )}
        </Col>
      </Row>
    </Input.Group>
  )
}

const CatalogueToolbar = styled.div`
  width: 100%;
  margin-bottom: 16px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`

const CatalogueSearchInput = styled(Input)`
  flex: 1 1 200px;
  min-width: 160px;
  max-width: 420px;
`

const FilterSelect = styled(Select)`
  flex: 0 1 200px;
  min-width: 150px;
  max-width: 260px;
`

const CatalogueResultCount = styled.span`
  margin-left: auto;
  font-size: 14px;
  color: #595959;
  white-space: nowrap;
`

const CatalogueSearchError = styled.p`
  flex: 1 0 100%;
  margin: 0;
  color: ${({ theme }) => theme.colours.chilliRed};
`

const SearchResultCount = styled.p`
  min-height: 40px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  text-align: right;
`

const SearchError = styled.p`
  color: ${({ theme }) => theme.colours.chilliRed};
`

export default SearchBar
